import { inject, Injectable } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { MatPaginator } from '@angular/material/paginator';
import { SET_VALUES_CONSTRUCTOR } from './token';

type TableServiceType = {
  __classRow?: string;
};

@Injectable()
export class TableService<Type = {}> {
  /**
   * @description El token sirve para poder sobrescribir la autocreacion de
   * las listas por medio de filtros. Esto solo sera necesario
   * @protected
   */
  protected readonly SET_VALUES_CONSTRUCTOR = inject(SET_VALUES_CONSTRUCTOR);
  form = new FormGroup({
    search: new FormControl<string>(undefined),
    date: new FormControl<Date | null>(null), // nuevo control separado
  });
  globalFilter$: Observable<string>;

  protected _dateFilter = new BehaviorSubject<Date | null>(null);
  dateFilter$ = this._dateFilter.asObservable();

  set dateFilter(value: Date | null) {
    this._dateFilter.next(value);
  }

  protected _filter = new BehaviorSubject<string>('');

  filter$ = this.form.get('search').valueChanges.pipe(
    startWith(''),
    switchMap((search) => {
      if (this.globalFilter$) {
        return this.globalFilter$.pipe(
          startWith(''),
          map((globalFilter) => {
            return globalFilter || search;
          })
        );
      }
      return of(search);
    }),
    tap((filter) => this._filter.next(filter))
  );

  get list(): Type[] {
    return this._list.getValue();
  }

  set list(value: Type[]) {
    this._list.next(value);
  }

  protected _list = new BehaviorSubject<Type[]>([]);
  list$: Observable<(Type & TableServiceType)[]>;
  listFiltered$: Observable<(Type & TableServiceType)[]>;

  protected _sortingOptions = new BehaviorSubject<{
    property: string;
    asc: boolean;
  }>(undefined);
  sortingOptions$ = this._sortingOptions.asObservable();

  set sortingOptions(value: { property: string; asc: boolean }) {
    this._sortingOptions.next(value);
  }

  protected _paginator = new BehaviorSubject<MatPaginator>(undefined);
  paginator$ = this._paginator.asObservable();

  set paginator(value: MatPaginator) {
    this._paginator.next(value);
  }

  dataSource$: Observable<MatTableDataSource<Type>>;
  protected _isAfterViewInit = new BehaviorSubject(false);
  isAfterViewInit$ = this._isAfterViewInit.asObservable();

  set isAfterViewInit(value: boolean) {
    this._isAfterViewInit.next(value);
  }

  protected _displayedColumns = new BehaviorSubject<string[]>([]);
  displayedColumns$ = combineLatest([
    this._displayedColumns.asObservable(),
    this.isAfterViewInit$,
  ]).pipe(map(([col, value]) => (value ? col : [])));

  set displayedColumns(value: string[]) {
    this._displayedColumns.next(value);
  }

  get displayedColumns(): string[] {
    return this._displayedColumns.getValue();
  }

  protected _itemSelected = new BehaviorSubject<Type>(undefined);
  itemSelected$ = this._itemSelected.asObservable();

  set itemSelected(value: Type) {
    this._itemSelected.next(value);
  }

  protected _sizePagination = new BehaviorSubject(100);
  sizePagination$ = this._sizePagination.asObservable();

  set sizePagination(value: number) {
    this._sizePagination.next(value);
  }

  filterItem: (item: Type, valueFilter: string) => boolean = undefined;
  sortingItem: (
    a: Type,
    b: Type,
    sortProperty: string,
    isAsc: boolean
  ) => number = undefined;

  classRow = (item: Type): string => undefined;

  constructor() {
    if (this.SET_VALUES_CONSTRUCTOR) {
      this.settingUpVariables();
    }
  }

  sort(sort: Sort): void {
    if (!sort.active || sort.direction === '') {
      this.sortingOptions = undefined;
    } else {
      this.sortingOptions = {
        property: sort.active,
        asc: sort.direction === 'asc',
      };
    }
  }

  protected settingUpVariables(): void {
    this.list$ = this.createListObservable();
    this.listFiltered$ = this.createListFilteredObservable();
    this.dataSource$ = this.createDataSourceObservable();
  }

  protected setClassRow(
    item: Type & TableServiceType
  ): Type & TableServiceType {
    if (this.classRow) {
      item.__classRow = this.classRow(item);
    }
    return item;
  }

  protected updateItem(val: { itemId: number; value: Partial<Type> }): void {
    let find = false;
    const newList = (this._list.getValue() as (Type & { id: number })[]).map(
      (el) => {
        if (el.id === val.itemId) {
          find = true;
          Object.assign(el, val.value);
        }
        return el;
      }
    );
    if (find) {
      this.list = newList;
    }
  }

  /**
   * @description
   * Este metodo tiene el objetivo de reutilizarse en los diferentes servicios que lleguen a necesitar
   * aplicar diferentes filtros al listado de items. Poner toda la logica dentro de una funcion
   * permite que no existan problemas de que cuando te suscribas a $listFiltered por accidente
   * se ejecute el item del TableService en vez del que realmente necesitas
   * @protected
   */
  protected createListObservable(): Observable<Type[]> {
    return this._list.asObservable().pipe(
      map((list) => {
        return list.map((item) => this.setClassRow(item));
      })
    );
  }

  /**
   * @description
   * Este metodo tiene el objetivo de reutilizarse en los diferentes servicios que lleguen a necesitar
   * aplicar diferentes filtros al listado de items. Poner toda la logica dentro de una funcion
   * permite que no existan problemas de que cuando te suscribas a $listFiltered por accidente
   * se ejecute el item del TableService en vez del que realmente necesitas
   * @protected
   */
  protected createListFilteredObservable(): Observable<
    (Type & TableServiceType)[]
  > {
    return combineLatest([this.list$, this.filter$, this.dateFilter$]).pipe(
      map(([list, search, date]) => {
        return list.filter((item) => {
          const matchTexto = search
            ? this.filterItem?.(item, search) ?? true
            : true;

          const matchFecha =
            date && 'createdAt' in item
              ? new Date((item as any).createdAt).toDateString() ===
                new Date(date).toDateString()
              : true;

          return matchTexto && matchFecha;
        });
      })
    );
  }

  protected createDataSourceObservable(): Observable<MatTableDataSource<Type>> {
    return combineLatest([
      this.listFiltered$,
      this.sortingOptions$,
      this.paginator$,
    ]).pipe(
      map(([filteredList, sortingOptions, paginator]) => {
        let sorted = filteredList;

        if (sortingOptions) {
          sorted = [...filteredList].sort((a, b) =>
            this.sortingItem(a, b, sortingOptions.property, sortingOptions.asc)
          );
        }

        const dataSource = new MatTableDataSource(sorted);
        dataSource.paginator = paginator;
        dataSource._updateChangeSubscription();
        return dataSource;
      })
    );
  }

  protected _fechaFiltro$ = new BehaviorSubject<string | null>(null);

  download(): void {}
}
